import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";
import { Leaderboard } from "@/components/Leaderboard";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { ArrowLeft, FileDown, FileText, Clock3 } from "lucide-react";

import { typeColor } from "@/lib/typeColors";

const colorForType = (type, settings) => typeColor(type, settings);

const MONTHS_IT = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const fmtMonth = (m) => {
  const [y, mo] = String(m).split("-");
  const idx = Number(mo) - 1;
  return idx >= 0 && idx < 12 ? `${MONTHS_IT[idx]} ${y}` : m;
};

export default function ReportPage() {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [chartTypes, setChartTypes] = useState([]);
  const [stacked, setStacked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reports/hours").then(({ data }) => { setRows(data); setLoading(false); }).catch(() => setLoading(false));
    api.get("/reports/hours/monthly").then(({ data }) => { setMonthly(data.data || []); setChartTypes(data.types || []); }).catch(() => {});
  }, []);

  const totalHours = rows.reduce((a, r) => a + r.hours, 0);

  const download = async (fmt) => {
    try {
      const res = await api.get("/reports/hours/export", { params: { format: fmt }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_ore.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore durante l'esportazione");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="cri-topbar" />
      <header className="sticky top-0 z-40 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <Button variant="ghost" size="icon" data-testid="report-back" onClick={() => navigate("/")} className="rounded-full">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <BrandLogo logoUrl={settings.logo_url} size={32} />
          <div>
            <p className="font-display font-bold">Report Ore</p>
            <p className="text-xs text-muted-foreground">{settings.committee_name}</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">Ore di servizio</h1>
            <p className="text-muted-foreground mt-1 flex items-center gap-2">
              <Clock3 className="h-4 w-4" /> Totale comitato: <strong>{Math.round(totalHours * 100) / 100} h</strong>
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="export-csv" onClick={() => download("csv")} className="rounded-full font-semibold">
              <FileDown className="h-4 w-4 mr-1" /> CSV
            </Button>
            <Button variant="outline" data-testid="export-pdf" onClick={() => download("pdf")} className="rounded-full font-semibold">
              <FileText className="h-4 w-4 mr-1" /> PDF
            </Button>
          </div>
        </div>

        <Tabs defaultValue="riepilogo">
          <TabsList data-testid="report-tabs">
            <TabsTrigger value="riepilogo" data-testid="tab-riepilogo">Riepilogo</TabsTrigger>
            <TabsTrigger value="grafico" data-testid="tab-grafico">Grafico mensile</TabsTrigger>
          </TabsList>

          <TabsContent value="riepilogo" className="mt-6">
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              {loading ? (
                <p className="p-8 text-center text-muted-foreground">Caricamento…</p>
              ) : rows.length === 0 ? (
                <p className="p-8 text-center text-muted-foreground">Nessuna ora registrata. Inserisci il consuntivo ore dai servizi svolti.</p>
              ) : (
                <Table data-testid="report-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Volontario</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-right">Servizi</TableHead>
                      <TableHead className="text-right">Ore previste</TableHead>
                      <TableHead className="text-right">Ore effettive</TableHead>
                      <TableHead className="text-right">Scostamento</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.user_id} data-testid={`report-row-${r.user_id}`}>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell className="text-muted-foreground">{r.email}</TableCell>
                        <TableCell className="text-right">{r.services}</TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">{r.expected}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">{r.hours}</TableCell>
                        <TableCell data-testid={`report-diff-${r.user_id}`} className={`text-right font-mono font-semibold ${r.diff > 0 ? "text-emerald-600" : r.diff < 0 ? "text-red-600" : "text-muted-foreground"}`}>
                          {r.diff > 0 ? "+" : ""}{r.diff}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="grafico" className="mt-6">
            <div className="rounded-xl border border-border bg-card p-6" data-testid="report-chart">
              {monthly.length === 0 ? (
                <p className="p-8 text-center text-muted-foreground">Nessun dato disponibile per il grafico.</p>
              ) : (
                <>
                  <div className="flex items-center justify-end gap-2 mb-4">
                    <span className="text-xs text-muted-foreground mr-1">Vista:</span>
                    <div className="inline-flex rounded-full border border-border p-0.5 bg-muted/40">
                      <button
                        type="button"
                        data-testid="chart-grouped-btn"
                        onClick={() => setStacked(false)}
                        className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${!stacked ? "bg-[#D32F2F] text-white" : "text-muted-foreground hover:text-foreground"}`}
                      >
                        Raggruppate
                      </button>
                      <button
                        type="button"
                        data-testid="chart-stacked-btn"
                        onClick={() => setStacked(true)}
                        className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${stacked ? "bg-[#D32F2F] text-white" : "text-muted-foreground hover:text-foreground"}`}
                      >
                        Impilate
                      </button>
                    </div>
                  </div>
                  <div style={{ width: "100%", height: 380 }}>
                    <ResponsiveContainer>
                      <BarChart data={monthly} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} unit=" h" />
                        <Tooltip labelFormatter={fmtMonth} formatter={(v, name) => [`${v} h`, name]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {chartTypes.map((t, i) => {
                          const isLast = i === chartTypes.length - 1;
                          return (
                            <Bar
                              key={t}
                              dataKey={t}
                              name={t}
                              fill={colorForType(t, settings)}
                              stackId={stacked ? "ore" : undefined}
                              radius={stacked ? (isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]) : [4, 4, 0, 0]}
                            />
                          );
                        })}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
        <Leaderboard />
      </main>
    </div>
  );
}
